import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Topic, UpdateTopicInput, CreateTopicInput } from '@anchor-comms/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function TopicsPage() {
  const qc = useQueryClient();
  const topicsQuery = useQuery({
    queryKey: ['topics', 'all'],
    queryFn: () => api<Topic[]>('/api/v1/topics', { query: { includeInactive: true } }),
  });

  const create = useMutation({
    mutationFn: (input: CreateTopicInput) =>
      api<Topic>('/api/v1/topics', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['topics'] }),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; input: UpdateTopicInput }) =>
      api<Topic>(`/api/v1/topics/${vars.id}`, { method: 'PATCH', body: vars.input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['topics'] }),
  });

  const [draft, setDraft] = useState<CreateTopicInput>({
    name: '',
    slug: '',
    description: '',
    guidancePrompt: '',
    displayOrder: 0,
    isActive: true,
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Topics</h1>

      <div className="space-y-3">
        {topicsQuery.data?.map((t) => (
          <TopicEditor key={t.id} topic={t} onSave={(input) => update.mutate({ id: t.id, input })} />
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New topic</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <Label>Slug (kebab-case)</Label>
              <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={draft.description ?? ''}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Guidance prompt</Label>
              <Textarea
                rows={5}
                value={draft.guidancePrompt ?? ''}
                onChange={(e) => setDraft({ ...draft, guidancePrompt: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              onClick={() =>
                create.mutate(draft, {
                  onSuccess: () =>
                    setDraft({
                      name: '',
                      slug: '',
                      description: '',
                      guidancePrompt: '',
                      displayOrder: 0,
                      isActive: true,
                    }),
                })
              }
              disabled={!draft.name || !draft.slug || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create topic'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TopicEditor({ topic, onSave }: { topic: Topic; onSave: (input: UpdateTopicInput) => void }) {
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState<UpdateTopicInput>({});

  const current = { ...topic, ...edits };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">
          {topic.name}
          <span className="ml-2 text-xs text-muted-foreground">{topic.slug}</span>
          {!topic.isActive && <span className="ml-2 text-xs text-destructive">inactive</span>}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : 'Edit'}
        </Button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={current.name ?? ''} onChange={(e) => setEdits({ ...edits, name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={current.description ?? ''}
              onChange={(e) => setEdits({ ...edits, description: e.target.value })}
            />
          </div>
          <div>
            <Label>Guidance prompt</Label>
            <Textarea
              rows={6}
              value={current.guidancePrompt ?? ''}
              onChange={(e) => setEdits({ ...edits, guidancePrompt: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={current.isActive ?? true}
              onChange={(e) => setEdits({ ...edits, isActive: e.target.checked })}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEdits({});
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSave(edits);
                setEdits({});
                setOpen(false);
              }}
              disabled={Object.keys(edits).length === 0}
            >
              Save
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
